import type { TipoVenta, ProductType } from "@/domain/sm-models";
import { formatSaleQty, getSaleQtyRules, isValidSaleQty } from "@/lib/sale-qty-rules";
import {
  taxLineFromAmount,
  aggregateLineBreakdowns,
  toTaxSnapshot,
  type LineTaxBreakdown,
  type TaxCategory,
  type PriceMode,
} from "@/lib/tax";

/** Normaliza columnas JSONB que a veces llegan como string (doble serialización en seeds). */
export function coerceJsonbArray<T>(value: unknown): T[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value as T[];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export interface MarketOrderAddonInput {
  addonProductId: string;
  cantidad: number;
  /** Variante elegida (uso: desglose de sabores por producto dentro de un combo, ver #22). */
  variantId?: string;
}

export interface MarketOrderItemInput {
  productId?: string;
  productName: string;
  tipoVenta: TipoVenta;
  cantidad: number;
  unidadMedida: string;
  precioUnitario?: number;
  precioPorKg?: number;
  variantId?: string | null;
  variantName?: string | null;
  /** SCRUM-91: presentación elegida (six pack, caja x24...). */
  presentationId?: string | null;
  presentationName?: string | null;
  presentationUnitFactor?: number;
  addons?: Array<MarketOrderAddonInput | { addonProductId: string; name: string; cantidad: number; precioUnitario: number }>;
  /** Present when the line is a Bundle pack (parent). */
  bundle?: {
    bundleId: string;
    bundleSlug?: string;
    version?: number;
    discountTotal?: number;
    listSubtotal?: number;
    appliedRuleKeys?: string[];
    message?: string;
  };
  bundleId?: string;
  bundleVersion?: number;
  bundleDiscount?: number;
  lineKind?: "product" | "combo" | "bundle";
  /** Tax Engine snapshot (neto / IVA / total). */
  taxBreakdown?: Record<string, unknown> | null;
}

/** Variante mínima para re-pricing server-side. */
export interface CatalogVariantRow {
  id: string;
  productId: string;
  name: string;
  precioUnitario: number | null;
  precioPorKg: number | null;
  activo: boolean;
}

/** Presentación mínima para re-pricing server-side (SCRUM-91). */
export interface CatalogPresentationRow {
  id: string;
  productId: string;
  name: string;
  unitFactor: number;
  price: number | null;
}

/** Addon permitido (relación) para validar que el extra pertenezca al producto. */
export interface CatalogAddonRow {
  productId: string;
  addonProductId: string;
}

/** Combo mínimo para re-pricing server-side (no se confía en el precio del cliente). */
export interface CatalogComboRow {
  id: string;
  name: string;
  price: number;
  active: boolean;
  items: Array<{ productId: string; quantity: number }>;
}

/** Bundle published snapshot mínimo para re-pricing server-side. */
export interface CatalogBundleRow {
  id: string;
  name: string;
  slug: string;
  version: number;
  active: boolean;
  /** Max % discount allowed by published rules (0 if none). */
  maxDiscountPercent: number;
  fixedPrice?: number | null;
  allowDiscounts?: boolean;
}

export interface ValidateMarketOptions {
  /** Variantes indexadas por id. */
  variantsById?: Map<string, CatalogVariantRow>;
  /** addons permitidos: Set de `${productId}|${addonProductId}`. */
  allowedAddons?: Set<string>;
  /** Combos indexados por id — un ítem de carrito puede referenciar un combo en vez de un producto. */
  combosById?: Map<string, CatalogComboRow>;
  /** Bundles publicados indexados por id. */
  bundlesById?: Map<string, CatalogBundleRow>;
  /** Presentaciones indexadas por id (SCRUM-91). */
  presentationsById?: Map<string, CatalogPresentationRow>;
}

interface NormalizedAddon {
  addonProductId: string;
  name: string;
  cantidad: number;
  precioUnitario: number;
  variantId?: string;
  variantName?: string;
}

export interface CatalogProductRow {
  id: string;
  name: string;
  productType?: ProductType;
  tipoVenta: TipoVenta;
  precioUnitario: number | null;
  precioPorKg: number | null;
  unidadMedida: string;
  minQty?: number | null;
  qtyStep?: number | null;
  activo: boolean;
  categoriaId: string | null;
  /** Fiscal — Tax Engine (default priceMode=final). Widened to string for row mappers. */
  priceMode?: string | null;
  taxCategory?: string | null;
  taxRate?: number | null;
}

export function filterProductsByActiveCategories(
  products: CatalogProductRow[],
  categoriasActivas: Array<{ id: string; active?: boolean }> | unknown,
): CatalogProductRow[] {
  const cats = coerceJsonbArray<{ id: string; active?: boolean }>(categoriasActivas);
  if (!cats.length) return products;
  const inactive = new Set(
    cats.filter((c) => c.active === false).map((c) => c.id),
  );
  if (!inactive.size) return products;
  return products.filter((p) => !p.categoriaId || !inactive.has(p.categoriaId));
}

export function validateMarketOrderItems(
  items: MarketOrderItemInput[],
  products: CatalogProductRow[],
  options?: ValidateMarketOptions,
): { ok: true; items: MarketOrderItemInput[]; precioEstimado: number; tienePesables: boolean; taxTotals: { netAmount: number; taxAmount: number; grossAmount: number; allRatesKnown: boolean } }
  | { ok: false; error: string } {
  if (!items.length) return { ok: false, error: "El pedido está vacío" };

  const byId = new Map(products.map((p) => [p.id, p]));
  const variantsById = options?.variantsById;
  const allowedAddons = options?.allowedAddons;
  const combosById = options?.combosById;
  // Precio de addons: los addons son productos del catálogo (is_addon) → su precio unitario.
  let precioEstimado = 0;
  let tienePesables = false;
  const normalized: MarketOrderItemInput[] = [];
  const lineTaxes: LineTaxBreakdown[] = [];

  for (const raw of items) {
    if (!raw.productId) return { ok: false, error: "Producto inválido en el pedido" };

    // Combo: no vive en sm_products, se resuelve aparte y se expande a sus ítems
    // incluidos (para comanda/recibo) sin ocupar la columna product_id (queda NULL —
    // ya es nullable, ver 001_schema_base.sql / sm_order_items_product_id_fkey).
    const combo = combosById?.get(raw.productId);
    if (combo) {
      if (!combo.active) return { ok: false, error: `Combo no disponible: ${raw.productName}` };
      const qty = Number(raw.cantidad);
      if (!Number.isFinite(qty) || qty <= 0) {
        return { ok: false, error: `Cantidad inválida para ${combo.name}` };
      }

      // Desglose de sabores/variantes por producto del combo (#22): si el
      // cliente envía una selección explícita, se valida contra la cantidad
      // fija de cada ítem del combo (combo.items[].quantity). Si no envía
      // nada (combo sin variantes, o flujo previo), se usa el desglose
      // default 1:1 con el catálogo del combo (comportamiento de #26).
      const clientBreakdown = raw.addons;
      let included: NormalizedAddon[];
      if (clientBreakdown && clientBreakdown.length > 0) {
        const requiredByProduct = new Map(combo.items.map((ci) => [ci.productId, ci.quantity]));
        const totals = new Map<string, number>();
        included = [];
        for (const a of clientBreakdown) {
          const required = requiredByProduct.get(a.addonProductId);
          if (required == null) {
            return { ok: false, error: `Producto no pertenece al combo: ${combo.name}` };
          }
          const aQty = Number(a.cantidad);
          if (!Number.isFinite(aQty) || aQty <= 0) {
            return { ok: false, error: `Cantidad inválida en la selección de ${combo.name}` };
          }
          totals.set(a.addonProductId, (totals.get(a.addonProductId) ?? 0) + aQty);

          const variantId = "variantId" in a ? a.variantId : undefined;
          let variantName: string | undefined;
          if (variantId) {
            const v = variantsById?.get(variantId);
            if (!v || !v.activo || v.productId !== a.addonProductId) {
              return { ok: false, error: `Variante no válida en la selección de ${combo.name}` };
            }
            variantName = v.name;
          }

          included.push({
            addonProductId: a.addonProductId,
            name: byId.get(a.addonProductId)?.name ?? a.addonProductId,
            cantidad: aQty,
            precioUnitario: 0,
            variantId,
            variantName,
          });
        }
        for (const [productId, required] of requiredByProduct) {
          if ((totals.get(productId) ?? 0) !== required) {
            return {
              ok: false,
              error: `Selección incompleta para ${byId.get(productId)?.name ?? productId} en ${combo.name}`,
            };
          }
        }
      } else {
        included = combo.items.map((ci) => ({
          addonProductId: ci.productId,
          name: byId.get(ci.productId)?.name ?? ci.productId,
          cantidad: ci.quantity,
          precioUnitario: 0,
        }));
      }
      precioEstimado += combo.price * qty;
      const comboTax = taxLineFromAmount(combo.price, qty, {
        priceMode: "final",
        taxCategory: "taxable",
        taxRate: null,
      });
      lineTaxes.push(comboTax);
      normalized.push({
        productId: undefined,
        productName: combo.name,
        tipoVenta: "unidad",
        cantidad: qty,
        unidadMedida: "unidad",
        precioUnitario: combo.price,
        variantId: null,
        variantName: null,
        addons: included,
        taxBreakdown: toTaxSnapshot(comboTax),
      });
      continue;
    }

    // Bundle: línea padre (productId = bundleId) + hijos en addons con precios de catálogo.
    const bundlesById = options?.bundlesById;
    const bundleMeta = raw.bundle;
    const bundleId = bundleMeta?.bundleId ?? (bundlesById?.has(raw.productId) ? raw.productId : null);
    if (bundleId && bundlesById?.has(bundleId)) {
      const bundle = bundlesById.get(bundleId)!;
      if (!bundle.active) return { ok: false, error: `Pack no disponible: ${raw.productName}` };
      const qty = Number(raw.cantidad);
      if (!Number.isFinite(qty) || qty <= 0) {
        return { ok: false, error: `Cantidad inválida para ${bundle.name}` };
      }
      const clientChildren = raw.addons ?? [];
      if (!clientChildren.length) {
        return { ok: false, error: `El pack ${bundle.name} no tiene productos` };
      }
      const included: NormalizedAddon[] = [];
      let listSubtotal = 0;
      for (const a of clientChildren) {
        const child = byId.get(a.addonProductId);
        if (!child || !child.activo) {
          return { ok: false, error: `Producto del pack no disponible` };
        }
        const aQty = Number(a.cantidad);
        if (!Number.isFinite(aQty) || aQty <= 0) {
          return { ok: false, error: `Cantidad inválida en ${bundle.name}` };
        }
        const unit = Number(child.precioUnitario ?? 0);
        listSubtotal += unit * aQty;
        included.push({
          addonProductId: child.id,
          name: child.name,
          cantidad: aQty,
          precioUnitario: unit,
        });
      }
      listSubtotal = Math.round(listSubtotal * 100) / 100;
      const claimed = Math.max(0, Number(bundleMeta?.discountTotal ?? 0));
      const fixed =
        bundle.fixedPrice != null && Number.isFinite(Number(bundle.fixedPrice))
          ? Number(bundle.fixedPrice)
          : null;
      let bundleDiscount: number;
      if (fixed != null) {
        const expected = Math.max(0, Math.round((listSubtotal - fixed) * 100) / 100);
        bundleDiscount = Math.min(claimed, expected);
      } else if (bundle.allowDiscounts === false) {
        bundleDiscount = 0;
      } else {
        const maxDto = Math.round(listSubtotal * (bundle.maxDiscountPercent / 100) * 100) / 100;
        bundleDiscount = Math.min(claimed, maxDto);
      }
      precioEstimado += listSubtotal * qty;
      normalized.push({
        productId: undefined,
        productName: bundle.name,
        tipoVenta: "unidad",
        cantidad: qty,
        unidadMedida: "unidad",
        precioUnitario: listSubtotal,
        variantId: null,
        variantName: null,
        addons: included,
        bundleId: bundle.id,
        bundleVersion: bundle.version,
        bundleDiscount,
        lineKind: "bundle",
        bundle: {
          bundleId: bundle.id,
          bundleSlug: bundle.slug,
          version: bundle.version,
          discountTotal: bundleDiscount,
          listSubtotal,
          message: bundleMeta?.message,
        },
      });
      {
        const bundleTax = taxLineFromAmount(listSubtotal, qty, {
          priceMode: "final",
          taxCategory: "taxable",
          taxRate: null,
        });
        lineTaxes.push(bundleTax);
        normalized[normalized.length - 1].taxBreakdown = toTaxSnapshot(bundleTax);
      }

      continue;
    }

    const product = byId.get(raw.productId);
    if (!product || !product.activo) {
      return { ok: false, error: `Producto no disponible: ${raw.productName}` };
    }
    const qty = Number(raw.cantidad);
    if (!Number.isFinite(qty) || qty <= 0) {
      return { ok: false, error: `Cantidad inválida para ${product.name}` };
    }
    if (qty > 10_000) {
      return { ok: false, error: `Cantidad máxima excedida para ${product.name} (máx. 10.000)` };
    }
    if (!isValidSaleQty(qty, product)) {
      const rules = getSaleQtyRules(product);
      return {
        ok: false,
        error: `Cantidad inválida para ${product.name}: mínimo ${formatSaleQty(rules.min, product)}, de a ${formatSaleQty(rules.step, product)}`,
      };
    }

    // Resolver variante (si se eligió) — re-pricing server-side.
    let variant: CatalogVariantRow | null = null;
    if (raw.variantId) {
      variant = variantsById?.get(raw.variantId) ?? null;
      if (!variant || !variant.activo || variant.productId !== product.id) {
        return { ok: false, error: `Variante no válida para ${product.name}` };
      }
    }

    // Resolver presentación (si se eligió) — re-pricing server-side (SCRUM-91).
    // Nunca se confía en el precio/factor que mande el cliente.
    let presentation: CatalogPresentationRow | null = null;
    if (raw.presentationId) {
      presentation = options?.presentationsById?.get(raw.presentationId) ?? null;
      if (!presentation || presentation.productId !== product.id) {
        return { ok: false, error: `Presentación no válida para ${product.name}` };
      }
    }

    // Resolver addons (re-pricing desde el catálogo; validar que estén permitidos).
    const addonsNorm: NormalizedAddon[] = [];
    let addonsPerUnit = 0;
    for (const a of raw.addons ?? []) {
      const addonProduct = byId.get(a.addonProductId);
      if (!addonProduct || !addonProduct.activo) {
        return { ok: false, error: `Extra no disponible en ${product.name}` };
      }
      if (allowedAddons && !allowedAddons.has(`${product.id}|${a.addonProductId}`)) {
        return { ok: false, error: `Extra no permitido en ${product.name}` };
      }
      const aQty = Number(a.cantidad);
      if (!Number.isFinite(aQty) || aQty <= 0) continue;
      const aPrice = Number(addonProduct.precioUnitario ?? 0);
      addonsPerUnit += aPrice * aQty;
      addonsNorm.push({ addonProductId: addonProduct.id, name: addonProduct.name, cantidad: aQty, precioUnitario: aPrice });
    }

    if (product.tipoVenta === "peso") {
      tienePesables = true;
      const baseKg = Number((variant?.precioPorKg ?? product.precioPorKg) ?? 0);
      const precioPorKg = presentation
        ? presentation.price ?? presentation.unitFactor * baseKg
        : baseKg;
      if (precioPorKg <= 0) return { ok: false, error: `Precio no configurado: ${product.name}` };
      precioEstimado += (precioPorKg + addonsPerUnit) * qty;
      const lineTaxPeso = taxLineFromAmount(precioPorKg + addonsPerUnit, qty, {
        priceMode: (product.priceMode === "net" ? "net" : "final") as PriceMode,
        taxCategory: (product.taxCategory as TaxCategory | null | undefined) ?? "taxable",
        taxRate: product.taxRate ?? null,
      });
      lineTaxes.push(lineTaxPeso);
      normalized.push({
        productId: product.id,
        productName: product.name,
        tipoVenta: "peso",
        cantidad: qty,
        unidadMedida: product.unidadMedida,
        precioPorKg,
        variantId: variant?.id ?? null,
        variantName: variant?.name ?? null,
        presentationId: presentation?.id ?? null,
        presentationName: presentation?.name ?? null,
        presentationUnitFactor: presentation?.unitFactor,
        addons: addonsNorm,
        taxBreakdown: toTaxSnapshot(lineTaxPeso),
      });
    } else {
      const baseUnit = Number((variant?.precioUnitario ?? product.precioUnitario) ?? 0);
      const precioUnitario = presentation
        ? presentation.price ?? presentation.unitFactor * baseUnit
        : baseUnit;
      if (precioUnitario <= 0) return { ok: false, error: `Precio no configurado: ${product.name}` };
      precioEstimado += (precioUnitario + addonsPerUnit) * qty;
      const lineTax = taxLineFromAmount(precioUnitario + addonsPerUnit, qty, {
        priceMode: (product.priceMode === "net" ? "net" : "final") as PriceMode,
        taxCategory: (product.taxCategory as TaxCategory | null | undefined) ?? "taxable",
        taxRate: product.taxRate ?? null,
      });
      lineTaxes.push(lineTax);
      normalized.push({
        productId: product.id,
        productName: product.name,
        tipoVenta: "unidad",
        cantidad: qty,
        unidadMedida: product.unidadMedida,
        precioUnitario,
        variantId: variant?.id ?? null,
        variantName: variant?.name ?? null,
        presentationId: presentation?.id ?? null,
        presentationName: presentation?.name ?? null,
        presentationUnitFactor: presentation?.unitFactor,
        addons: addonsNorm,
        taxBreakdown: toTaxSnapshot(lineTax),
      });
    }
  }

  const taxTotals = aggregateLineBreakdowns(lineTaxes);
  return { ok: true, items: normalized, precioEstimado, tienePesables, taxTotals };
}

/** Domicilio exige prepago (MP/transferencia). Retiro permite efectivo. */
export function assertPaymentMethodForDelivery(
  metodEntrega: "domicilio" | "retiro",
  paymentMethod: string,
): { ok: true } | { ok: false; error: string } {
  if (metodEntrega === "domicilio" && paymentMethod === "efectivo") {
    return {
      ok: false,
      error: "Los pedidos a domicilio requieren pago anticipado (transferencia, tarjeta, Mercado Pago o MODO).",
    };
  }
  return { ok: true };
}

export function filterPaymentMethodsForDelivery(
  methods: string[],
  metodEntrega: "domicilio" | "retiro",
): string[] {
  if (metodEntrega === "domicilio") {
    return methods.filter((m) => m !== "efectivo");
  }
  return methods;
}
